import { Link, useLocation, useParams, useHistory } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { interpolate } from 'utils/string';
import { handleError } from 'utils/errorHandler';
import { parse, updateUrl } from 'utils/queryParam';

import {
  NOTE_STATUS_TYPES,
  NOTE_STATUS_FILTER,
  DEFAULT_PAGE_NUMBER,
  EMPTY_NOTE_RESULT_MESSAGE,
  DEFAULT_NOTE_STATUS_FILTER,
} from 'constants/appConstants';
import * as routes from 'constants/routes';
import * as permissions from 'constants/permissions';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'constants/page';

import Empty from 'components/common/empty/Empty';
import EmployeeNoteList from './components/EmployeeNoteList';
import Pagination from 'components/common/paginator';
import Loading from 'components/common/loading/Loading';
import MaterialDropdown from 'components/common/dropDown/materialDropdown';
import { NoteItem } from 'interface/note';
import { OnChangeValue } from 'react-select';

import * as noteService from 'services/note';
import { useUserPermissions } from 'hooks/query/useUserInfo';

interface NoteListRouteParams {
  userId: string;
}

type NoteStatusOption = { label?: React.ReactNode; value: string | null };

interface NoteListProps {
  authUser: {
    id: number;
  };
}

interface NoteMeta {
  total: number;
  [key: string]: string | number;
}

const NoteList: React.FC<NoteListProps> = (props) => {
  const { userId = '' } = useParams<NoteListRouteParams>();
  const location = useLocation();
  const history = useHistory();
  const noteUserId = userId;
  const authUserId = props.authUser.id;
  const locationSearch = location.search || '';

  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [pageData, setPageData] = useState<NoteMeta>({ total: 0 });
  const [isFetchingNotes, setIsFetchingNotes] = useState(false);
  const [selectedPage, setSelectedPage] = useState(DEFAULT_PAGE);
  const [pageCount, setPageCount] = useState(DEFAULT_PAGE_NUMBER);
  const [selectedPageSize, setSelectedPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(NOTE_STATUS_TYPES.ACTIVE);

  const parsedQuery = useMemo(
    () =>
      parse(locationSearch, {
        decoder: (str: string) => decodeURIComponent(str),
      }),
    [locationSearch]
  );
  const selectedStatusValue = useMemo<NoteStatusOption | null>(
    () => NOTE_STATUS_FILTER.find((option) => option.value === selectedStatus) || null,
    [selectedStatus]
  );

  useEffect(() => {
    const fetchAllNotes = async () => {
      try {
        setIsFetchingNotes(true);
        const params = {
          userId: noteUserId,
          page: selectedPage,
          size: selectedPageSize,
          ...(selectedStatus ? { status: selectedStatus } : {}),
        };

        const { data, meta } = (await noteService.fetchNotes(params)) as { data: NoteItem[]; meta: NoteMeta };

        const { total } = meta;
        const pageCount = Math.ceil(total / selectedPageSize);

        setPageData(meta);
        setPageCount(pageCount);

        const notes = data.map((datum: NoteItem, index: number) => {
          return {
            ...datum,
            sn: (selectedPage - 1) * selectedPageSize + (index + 1),
          };
        });

        setNotes(notes);
      } catch (error) {
        handleError(error);
      } finally {
        setIsFetchingNotes(false);
      }
    };

    void fetchAllNotes();

    const nextQuery = {
      ...parsedQuery,
      page: selectedPage,
      size: selectedPageSize,
      ...(selectedStatus ? { status: selectedStatus } : {}),
    };

    if (!selectedStatus) {
      delete nextQuery.status;
    }

    updateUrl(
      {
        location: { pathname: location.pathname, search: location.search },
        history: { push: history.push },
      },
      nextQuery
    );
  }, [
    history.push,
    location.pathname,
    location.search,
    parsedQuery,
    noteUserId,
    selectedPage,
    selectedPageSize,
    selectedStatus,
  ]);

  const onPageChange = useCallback(
    (selectedPage: number) => {
      setSelectedPage(selectedPage);
    },
    [setSelectedPage]
  );

  const onPageSizeChange = useCallback(
    (selectedSize: number) => {
      setSelectedPageSize(selectedSize);
    },
    [setSelectedPageSize]
  );

  const userPermissions = useUserPermissions() as Record<string, boolean>;
  const handleFilterChange = (option: { status: string | null }) => {
    setSelectedStatus(option.status);
  };

  return (
    <main>
      <div className="container">
        <div className="full-scope-card">
          <div
            className={[
              'full-scope-card__header',
              'table-header',
              'name-wrap',
              'd-flex',
              'justify-content-between',
              'user-select-none',
            ].join(' ')}
          >
            <div>
              <span className="table-header__title">Notes</span>
            </div>
            <div className="d-flex">
              {React.createElement(MaterialDropdown as never, {
                isSearchable: false,
                options: NOTE_STATUS_FILTER,
                isPlain: true,
                isSmall: true,
                autoResize: true,
                value: selectedStatusValue || DEFAULT_NOTE_STATUS_FILTER,
                onChange: (option: OnChangeValue<NoteStatusOption, boolean>) => {
                  const selectedOption = Array.isArray(option) ? option[0] : option;
                  handleFilterChange({ status: selectedOption?.value as string | null });
                },
                customClassName: 'pr-40',
                placeholder: 'Status',
              })}
              {userPermissions[permissions.NOTES.CREATE] && (
                <Link
                  className="btn btn--primary btn--curved"
                  to={interpolate(routes.CREATE_EMPLOYEE_NOTE, { userId: noteUserId })}
                >
                  Add Note
                </Link>
              )}
            </div>
          </div>
          <div className="full-scope-card__content">
            {isFetchingNotes ? (
              <div className="full-scope-card mt-15">
                <Loading />
              </div>
            ) : notes.length ? (
              <>
                <EmployeeNoteList notes={notes.map((note) => ({ note }))} authUserId={authUserId} />
                <Pagination
                  pageData={pageData}
                  pageCount={+pageCount}
                  onPageChange={onPageChange}
                  onPageSizeChange={onPageSizeChange}
                />
              </>
            ) : (
              <div className="mt-15 p-20 empty-container__wrapper">
                <Empty message={EMPTY_NOTE_RESULT_MESSAGE} />
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default NoteList;
